<template>
  <LineChart
    :request-status="requestStatus"
    :series="series"
    :title="title"
    :item-id="itemId"
    class="make-a-copy"
    :card-options="cardOptions"
    :show-granularity="false"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import LineChart from '@/components/cards/models/LineChart';
import { mapState, mapActions } from 'vuex';
import cardsModel from '@/mixins/cards-model';
import EventBus from '@/utils/event-bus';
import { setDateToUTC } from '@/utils/date';
import blipModel from '@/mixins/blip-model';

export default {
  name: 'BlipEventTrackInTime',
  components: {
    LineChart,
  },
  mixins: [
    cardsModel,
    blipModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    isExpandedCard: {
      type: Boolean,
      default: false,
    },
    servicePath: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: [],
      cardOptions: {
        chart: {
          type: 'spline',
        },
        xAxis: {},
        yAxis: {
          min: null,
          plotLines: [{
            color: '#515156',
            width: 1,
            value: 0,
          }],
          labels: {
            enabled: true,
          },
        },
        legend: {
          enabled: true,
        },
        plotOptions: {
          series: {
            marker: {
              enabled: false,
              symbol: 'circle',
            },
          },
        },
      },
    };
  },
  computed: {
    ...mapState('filter', [
      'dateRange',
    ]),
    datePeriodFilter() {
      const arrayDate = [];

      for (let dateIterator = new Date(this.dateRange.start.valueOf());
        dateIterator.getTime() < this.dateRange.end.valueOf();
        dateIterator.setDate(dateIterator.getDate() + 1)) {
        arrayDate.push({
          x: dateIterator.getTime(),
          y: 0,
        });
      }

      return arrayDate;
    },
    series() {
      if (Boolean(this.rawSeries?.length)) {
        const seriesObject = this.rawSeries.reduce((seriesObject, { action, count, storageDate }) => {
          if (!Boolean(seriesObject[action])) {
            seriesObject[action] = [];
          }

          seriesObject[action].push({ storageDate, count });

          return seriesObject;
        }, {});

        const processSeriesObject = Object.keys(seriesObject)
          .map(name => {
            const dataSerie = seriesObject[name]
              .map(({ storageDate, count }) => ({
                x: setDateToUTC(storageDate).getTime(),
                y: count,
              }));

            const dataSerieInPeriod = dataSerie
              .concat(this.datePeriodFilter
                .filter(({ x }) => dataSerie.every(serie => serie.x !== x)))
              .sort((actualValue, prevValue) => actualValue.x - prevValue.x);

            return {
              name,
              data: dataSerieInPeriod,
            };
          });

        return processSeriesObject;
      }
      return [];
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getBlipService',
      'hasNoBlipBotAndContractFiltered',
      'getAppliedCardFilter',
    ]),
    async getData(options) {
      const appliedCardFilter = await this.getAppliedCardFilter(this.itemId);

      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      if (await this.hasNoBlipBotAndContractFiltered(this.itemId)) {
        this.requestStatus = 'emptyBot';
        return;
      }

      if (!this.isSingleBotSelected(appliedCardFilter)) {
        this.requestStatus = 'supportsOnlyOneBot';
        return;
      }

      this.getBlipService({
        servicePath: this.servicePath,
        itemId: this.itemId,
      })
        .then(response => {
          const responseBlip = this.returnBlipSingleBotResponse(response).resource?.items;
          if (Boolean(responseBlip) && Boolean(response.length)) {
            const actionCounts = responseBlip
              .reduce((accumulator, item) => {
                const { action, count, storageDate } = item;
                if (accumulator.has(action)) {
                  const existingData = accumulator.get(action);
                  existingData.count += count;
                } else {
                  accumulator.set(action, { count, storageDate });
                }
                return accumulator;
              }, new Map());

            const sortedActions = [...actionCounts.entries()].sort(
              (a, b) => b[1].count - a[1].count
            );

            const topFiftyActions = sortedActions
              .slice(0, 50)
              .map(([action]) => action);

            this.rawSeries = responseBlip.filter(item => topFiftyActions.includes(item.action));
          }

          this.requestStatus = 'success';

          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
        })
        .catch(error => {
          console.error(error);
          this.requestStatus = error.message;
        });
    },
  },
};
</script>
